import { Input } from "@magnetic/input";
import { Notification } from "@magnetic/notification";
import { Spinner } from "@magnetic/spinner";
import { Text } from "@magnetic/text";

import { ErrorNotification } from "src/components/errorNotification";
import { HFlex } from "src/components/hFlex";
import { VFlex } from "src/components/vFlex";
import { ApiError } from "src/core";
import { LinkedAccountCandidate } from "src/gen/schema/bilge/api";

import { getPlatformLabel } from "./botUtils";

interface BindingCodeDetailsProps {
  readonly passcode: string;
  readonly passcodeError: string | undefined;
  readonly error: ApiError | undefined;
  readonly validating: boolean;
  readonly candidateInfo: LinkedAccountCandidate | undefined;
  readonly selectedBotName: string;
  readonly onPasscodeChange: (e: React.FormEvent<HTMLInputElement>) => void;
}

export const BindingCodeDetails = ({
  passcode,
  passcodeError,
  error,
  validating,
  candidateInfo,
  selectedBotName,
  onPasscodeChange
}: BindingCodeDetailsProps) => {
  if (!candidateInfo) {
    return (
      <VFlex gap={12} className="binding-code-section">
        <Input
          label="Enter binding code"
          name="passcode"
          value={passcode}
          onChange={onPasscodeChange}
          placeholder="Use the code provided by the service app user"
          required={true}
          autoComplete="off"
        />

        {validating && (
          <HFlex align="center" gap={8}>
            <Spinner size="sm" />
            <Text size="p3">Validating passcode...</Text>
          </HFlex>
        )}

        <ErrorNotification error={error} msg={passcodeError} />
      </VFlex>
    );
  }

  return (
    <VFlex gap={12} className="binding-code-section">
      <Input
        label="Enter binding code"
        name="passcode"
        value={passcode}
        onChange={onPasscodeChange}
        placeholder="Enter binding code from chat platform"
        required={true}
        tooltip="Use the code provided by the service app user."
        autoComplete="off"
      />

      <Notification status="positive">Binding code is valid</Notification>

      <Text size="p3" title="Confirm that.">
        Confirm that you want to bind <strong>{selectedBotName}</strong> with
        the following service session.
      </Text>

      <div className="service-details-container">
        <VFlex gap={8}>
          <HFlex>
            <Text size="p3" className="detail-title">
              Service
            </Text>
            <Text size="p3">
              {getPlatformLabel(candidateInfo.chatPlatform)}
            </Text>
          </HFlex>
          <HFlex align="flex-start">
            <Text size="p3" className="detail-title">
              Service Details
            </Text>
            <VFlex gap={8}>
              {candidateInfo.discordAccount && (
                <>
                  <Text size="p3">
                    {`Server: ${candidateInfo.discordAccount.guildName}; ${candidateInfo.discordAccount.guildId}`}
                  </Text>
                  <Text size="p3">
                    {`Channel: ${candidateInfo.discordAccount.channelName}; ${candidateInfo.discordAccount.channelId}`}
                  </Text>
                </>
              )}

              {candidateInfo.webexAccount && (
                <>
                  <Text size="p3">{`Space Name: ${candidateInfo.webexAccount.spaceName}`}</Text>
                  <Text size="p3">{`Space ID: ${candidateInfo.webexAccount.spaceId}`}</Text>
                </>
              )}
            </VFlex>
          </HFlex>
        </VFlex>
      </div>
    </VFlex>
  );
};
